/**
 * The phone verification gate (ELARA-1170): where `App` sends a member who must add a phone number.
 *
 * Every member needs a verified number. Onboarding will ask for it; until then, `App` redirects a member
 * held by `PhoneRequirementState.isRequired` (no verified phone, account more than a day old) to this
 * page from every authenticated route, as it does for the membership payment page, and releases them
 * once they verify. A page of its own rather than a dialog over another screen: there is nothing
 * behind it to use until the number is verified. Laid out as a single card on a plain page, with no
 * app chrome.
 *
 * The flow is the shared `PhoneLinkState`, the same store the Profile page's `PhoneVerificationDialog`
 * drives, so this is presentation over its `status`: editor → code step → verified. After verifying the
 * member stays here to see the success view (and add Elara to their contacts) until they continue.
 * Mobile holds the same members with a full-screen `PhoneRequiredGate`.
 */

import { useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { SUPPORT_EMAIL } from '@elara/client-core/lib/support-chat';

import { useNavigate, usePhoneLinkState, useSessionState } from '#states/hooks.js';
import { Alert, Button, FloatingInput, Logo, MaterialIcon } from '#ui/index.js';
import { PhoneInput } from '#ui/PhoneInput.js';

/** Where "Contact us" goes when a member can't verify a number. */
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Adding my phone number')}`;

export function VerifyPhonePage() {
  const phoneLink = usePhoneLinkState();
  const sessionState = useSessionState();
  const { navigate } = useNavigate();
  const [code, setCode] = useState('');

  const status = phoneLink.status;
  const inCodeStep = status === 'awaitingCode' || status === 'verifying';
  const justVerified = status === 'verified';

  // Shown above the input on whichever step raised it: a bad number, or a wrong or expired code.
  const errorBanner = phoneLink.error && <Alert variant="destructive">{phoneLink.error}</Alert>;

  const handleConfirm = async () => {
    await phoneLink.confirmCode(code);
    if (phoneLink.status === 'verified') setCode('');
  };

  const handleChangeNumber = () => {
    setCode('');
    phoneLink.beginEdit();
  };

  const handleContinue = () => {
    // Back to idle, so the Profile dialog opens on its own manage view later.
    phoneLink.closeSheet();
    navigate(ROUTES.HOME, { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background-secondary safe-area-top">
      {/* Padded as `AppLayout`'s sidebar pads its logo. The full logo opens with the same 32px symbol the
          app shows, so the symbol stays put when the member is let into the app. */}
      <header className="px-3 py-2 md:px-2 md:py-4">
        <div className="p-1">
          <Logo />
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md space-y-4">
          <div className="space-y-6 rounded-2xl border bg-background p-6 shadow-sm md:p-8">
            {justVerified ? (
              <>
                <div className="space-y-2">
                  <MaterialIcon icon="verified" size="xl" filled className="text-accent" />
                  <h1 className="text-2xl font-display">Phone number verified</h1>
                  <p className="text-lg">{phoneLink.verifiedPhone}</p>
                  <p className="text-sm text-muted-foreground">
                    You can message Elara from this number anytime with questions about your trips.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="lg" className="w-full" onClick={handleContinue}>
                    Continue
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full"
                    onClick={() => void phoneLink.addElaraToContacts()}
                  >
                    Add Elara to Contacts
                  </Button>
                </div>
              </>
            ) : inCodeStep ? (
              <>
                <div className="space-y-2">
                  <h1 className="text-2xl font-display">Enter your code</h1>
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code we sent to {phoneLink.pendingPhone || 'your phone'}.
                  </p>
                </div>
                {errorBanner}
                <FloatingInput
                  id="phoneVerificationCode"
                  label="6-digit code"
                  hideLabel
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                />
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => void handleConfirm()}
                  isBusy={status === 'verifying'}
                >
                  Confirm
                </Button>
                <div className="flex justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void phoneLink.sendCode()}
                    disabled={status === 'verifying'}
                  >
                    Resend code
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleChangeNumber} disabled={status === 'verifying'}>
                    Change number
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <h1 className="text-2xl font-display">Verify your phone number</h1>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      We’ll use this number if we need to contact you about a booking. You can also message Elara via
                      SMS using this number to get travel advice from wherever you are.
                    </p>
                  </div>
                </div>
                {errorBanner}
                <div className="space-y-2">
                  <PhoneInput
                    id="phoneNumber"
                    hideLabel
                    value={phoneLink.pendingPhone}
                    onChange={(value) => phoneLink.setPendingPhone(value)}
                  />
                  <p className="text-xs text-muted-foreground">We’ll text you a code to confirm your number.</p>
                </div>
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => void phoneLink.sendCode()}
                  isBusy={status === 'sendingCode'}
                >
                  Send code
                </Button>
              </>
            )}
          </div>

          {!justVerified && (
            <div className="flex justify-center gap-4 text-sm text-muted-foreground">
              <a href={SUPPORT_MAILTO} className="hover:text-foreground hover:underline">
                Contact us
              </a>
              <button
                type="button"
                onClick={() => void sessionState.logout()}
                disabled={phoneLink.isBusy}
                className="hover:text-foreground hover:underline disabled:opacity-50"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
